<template>
  <q-card flat bordered>
    <q-card-section>
      <div class="section-header">
        <div class="text-h6">Scope & Qualifications</div>
        <div class="action-buttons">
          <q-btn
            flat
            dense
            color="secondary"
            icon="library_add"
            label="Import from Library"
            :disable="loading"
            class="q-mr-sm"
            @click="showImportDialog = true"
          />
          <q-btn
            flat
            dense
            color="primary"
            icon="add"
            label="Add Custom"
            :disable="loading"
            @click="showCustomDialog = true"
          />
        </div>
      </div>

      <q-linear-progress v-if="loading" indeterminate color="primary" class="q-mt-md" />

      <div v-if="groupedQualifications && Object.keys(groupedQualifications).length > 0" class="q-mt-md">
        <q-list bordered separator>
          <q-expansion-item
            v-for="(items, category) in groupedQualifications"
            :key="category"
            :label="category || 'Other'"
            expand-separator
            icon="label"
            header-class="text-weight-bold"
          >
            <q-list>
              <q-item
                v-for="qualification in items"
                :key="qualification.id"
                tag="label"
              >
                <q-item-section avatar>
                  <q-checkbox
                    v-model="qualification.is_included"
                    :disable="loading"
                    @update:model-value="toggleQualification(qualification)"
                  />
                </q-item-section>

                <q-item-section>
                  <q-item-label>{{ qualification.text }}</q-item-label>
                  <q-item-label v-if="qualification.is_custom" caption>
                    <q-badge color="secondary" label="Custom" />
                  </q-item-label>
                </q-item-section>

                <q-item-section v-if="qualification.is_custom" side>
                  <q-btn
                    flat
                    round
                    dense
                    icon="delete"
                    size="sm"
                    color="negative"
                    :disable="loading"
                    @click.stop="confirmDeleteQualification(qualification)"
                  >
                    <q-tooltip>Remove Custom Qualification</q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </q-list>
          </q-expansion-item>
        </q-list>
      </div>

      <div v-else-if="!loading" class="empty-state q-pa-lg text-center">
        <q-icon name="rule" size="48px" color="grey-5" />
        <p class="text-grey-7 q-mt-md">No qualifications yet. Click "Add Custom" to get started.</p>
      </div>
    </q-card-section>
  </q-card>

  <!-- Add Custom Qualification Dialog -->
  <q-dialog v-model="showCustomDialog" persistent>
    <q-card style="min-width: 500px">
      <q-card-section>
        <div class="text-h6">Add Custom Qualification</div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-input
          v-model="customForm.text"
          label="Qualification Text *"
          outlined
          type="textarea"
          rows="3"
          maxlength="2000"
          counter
          :rules="[val => !!val || 'Text is required']"
          class="q-mb-md"
        />

        <q-input
          v-model="customForm.category"
          label="Category (optional)"
          outlined
          class="q-mb-md"
          placeholder="e.g., Pricing Terms, Site Requirements"
        />

        <q-checkbox
          v-model="customForm.add_to_global"
          label="Add to global library for future use"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="closeCustomDialog" />
        <q-btn
          color="primary"
          label="Add"
          :loading="saving"
          :disable="!customForm.text"
          @click="addCustomQualification"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Delete Confirmation Dialog -->
  <q-dialog v-model="showDeleteDialog" persistent>
    <q-card>
      <q-card-section class="row items-center">
        <q-avatar icon="warning" color="negative" text-color="white" />
        <span class="q-ml-sm">Are you sure you want to remove this custom qualification?</span>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="showDeleteDialog = false" />
        <q-btn
          flat
          label="Remove"
          color="negative"
          :loading="deleting"
          @click="deleteQualification"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Import from Library Dialog -->
  <q-dialog v-model="showImportDialog" persistent maximized>
    <q-card>
      <q-card-section class="bg-primary text-white">
        <div class="text-h6">Import Qualifications from Library</div>
      </q-card-section>

      <q-card-section>
        <q-linear-progress v-if="loadingLibrary" indeterminate color="primary" class="q-mb-md" />

        <div v-if="!loadingLibrary && libraryQualifications.length === 0" class="text-center q-pa-lg">
          <q-icon name="info" size="48px" color="grey-5" />
          <p class="text-grey-7 q-mt-md">No qualifications available in the library.</p>
        </div>

        <div v-else-if="!loadingLibrary" class="import-content">
          <q-list bordered separator>
            <q-expansion-item
              v-for="(items, category) in groupedLibraryQualifications"
              :key="category"
              :label="category || 'Other'"
              expand-separator
              icon="label"
              header-class="text-weight-bold"
            >
              <template #header>
                <q-item-section avatar>
                  <q-icon name="label" color="primary" />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-weight-bold">{{ category || 'Other' }}</q-item-label>
                  <q-item-label caption>{{ items.length }} qualifications</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <div class="row q-gutter-xs">
                    <q-btn
                      flat
                      dense
                      size="sm"
                      label="Select All"
                      color="primary"
                      :disable="allSelectedInCategory(category)"
                      @click.stop="selectAllInCategory(category)"
                    />
                    <q-btn
                      flat
                      dense
                      size="sm"
                      label="Deselect All"
                      :disable="noneSelectedInCategory(category)"
                      @click.stop="deselectAllInCategory(category)"
                    />
                  </div>
                </q-item-section>
              </template>

              <q-list>
                <q-item
                  v-for="qualification in items"
                  :key="qualification.id"
                  tag="label"
                  :disable="isAlreadyInTender(qualification)"
                >
                  <q-item-section avatar>
                    <q-checkbox
                      v-model="selectedImportIds"
                      :val="qualification.id"
                      :disable="isAlreadyInTender(qualification)"
                    />
                  </q-item-section>

                  <q-item-section>
                    <q-item-label>{{ qualification.qualification_text }}</q-item-label>
                    <q-item-label v-if="isAlreadyInTender(qualification)" caption>
                      <q-badge color="info" label="Already in tender" />
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-expansion-item>
          </q-list>
        </div>
      </q-card-section>

      <q-card-actions align="right" class="bg-grey-2">
        <q-btn flat label="Cancel" @click="closeImportDialog" />
        <q-btn
          color="primary"
          label="Import Selected"
          icon="download"
          :loading="importing"
          :disable="selectedImportIds.length === 0"
          @click="importSelected"
        >
          <q-badge v-if="selectedImportIds.length > 0" color="red" floating>{{ selectedImportIds.length }}</q-badge>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    qualifications: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['toggled', 'custom-added']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const saving = ref(false);
  const deleting = ref(false);
  const loadingLibrary = ref(false);
  const importing = ref(false);
  const internalQualifications = ref([...props.qualifications]);
  const showCustomDialog = ref(false);
  const showDeleteDialog = ref(false);
  const showImportDialog = ref(false);
  const qualificationToDelete = ref(null);
  const libraryQualifications = ref([]);
  const selectedImportIds = ref([]);

  const customForm = ref({
    text: '',
    category: '',
    add_to_global: false,
  });

  // Computed
  const groupedQualifications = computed(() => {
    const groups = {};

    internalQualifications.value.forEach((qual) => {
      const category = qual.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(qual);
    });

    return groups;
  });

  const groupedLibraryQualifications = computed(() => {
    const groups = {};

    libraryQualifications.value.forEach((qual) => {
      const category = qual.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(qual);
    });

    return groups;
  });

  // Watch for prop changes
  watch(() => props.qualifications, (newQualifications) => {
    internalQualifications.value = [...newQualifications];
  }, { deep: true });

  // Methods
  async function toggleQualification(qualification) {
    const previousState = !qualification.is_included;

    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/toggle-qualification/${qualification.id}`, {
        method: 'PUT',
      });

      const data = await response.json();

      if (data.success) {
        emit('toggled', data.data);
      }
    } catch (error) {
      // Revert on error
      qualification.is_included = previousState;

      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to update qualification: ' + error.message,
          timeout: 4000,
        });
      }
    }
  }

  function closeCustomDialog() {
    showCustomDialog.value = false;
    customForm.value = {
      text: '',
      category: '',
      add_to_global: false,
    };
  }

  async function addCustomQualification() {
    if (!customForm.value.text.trim()) {
      $q.notify({
        type: 'warning',
        message: 'Please enter qualification text',
        timeout: 3000,
      });
      return;
    }

    saving.value = true;
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/add-custom-qualification`, {
        method: 'POST',
        body: JSON.stringify(customForm.value),
      });

      const data = await response.json();

      if (data.success) {
        internalQualifications.value.push(data.data);

        $q.notify({
          type: 'positive',
          message: 'Custom qualification added successfully',
          timeout: 2000,
        });

        emit('custom-added', data.data);
        closeCustomDialog();
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to add qualification: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      saving.value = false;
    }
  }

  function confirmDeleteQualification(qualification) {
    qualificationToDelete.value = qualification;
    showDeleteDialog.value = true;
  }

  async function deleteQualification() {
    if (!qualificationToDelete.value) {return;}

    deleting.value = true;
    try {
      // Call the DELETE API endpoint
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/custom-qualification/${qualificationToDelete.value.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        // Remove from local array
        const index = internalQualifications.value.findIndex((q) => q.id === qualificationToDelete.value.id);
        if (index > -1) {
          internalQualifications.value.splice(index, 1);
        }

        $q.notify({
          type: 'positive',
          message: 'Custom qualification removed successfully',
          timeout: 2000,
        });

        showDeleteDialog.value = false;
        qualificationToDelete.value = null;
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to remove qualification: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      deleting.value = false;
    }
  }

  // Import dialog functions
  async function loadLibraryQualifications() {
    loadingLibrary.value = true;
    try {
      const response = await authenticatedFetch('/api/settings/scope-qualifications');
      const data = await response.json();

      // Use 'all' property which contains the flat list of qualifications
      // The component will group them itself using groupedLibraryQualifications computed property
      libraryQualifications.value = data.success ? data.all : [];
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to load library: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      loadingLibrary.value = false;
    }
  }

  function isAlreadyInTender(qualification) {
    return internalQualifications.value.some((q) => q.scope_qualification_id === qualification.id);
  }

  function selectAllInCategory(category) {
    const categoryItems = groupedLibraryQualifications.value[category] || [];
    categoryItems.forEach((item) => {
      if (!isAlreadyInTender(item) && !selectedImportIds.value.includes(item.id)) {
        selectedImportIds.value.push(item.id);
      }
    });
  }

  function deselectAllInCategory(category) {
    const categoryItems = groupedLibraryQualifications.value[category] || [];
    const categoryIds = categoryItems.map((item) => item.id);
    selectedImportIds.value = selectedImportIds.value.filter((id) => !categoryIds.includes(id));
  }

  function allSelectedInCategory(category) {
    const categoryItems = groupedLibraryQualifications.value[category] || [];
    const selectableItems = categoryItems.filter((item) => !isAlreadyInTender(item));
    return selectableItems.length > 0 && selectableItems.every((item) => selectedImportIds.value.includes(item.id));
  }

  function noneSelectedInCategory(category) {
    const categoryItems = groupedLibraryQualifications.value[category] || [];
    return !categoryItems.some((item) => selectedImportIds.value.includes(item.id));
  }

  async function importSelected() {
    if (selectedImportIds.value.length === 0) {return;}

    importing.value = true;
    try {
      const response = await authenticatedFetch(`/api/tenders/${props.tenderId}/import-qualifications`, {
        method: 'POST',
        body: JSON.stringify({
          scope_qualification_ids: selectedImportIds.value,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state with newly imported qualifications
        if (data.data && data.data.scope_qualifications) {
          internalQualifications.value = data.data.scope_qualifications;
        } else if (Array.isArray(data.data)) {
          // Fallback for old API response format
          data.data.forEach((newQual) => {
            internalQualifications.value.push(newQual);
          });
        }

        $q.notify({
          type: 'positive',
          message: `Successfully imported ${selectedImportIds.value.length} qualification(s)`,
          timeout: 3000,
        });

        closeImportDialog();

        // Emit event to parent with the updated data
        if (data.data && data.data.scope_qualifications) {
          emit('qualifications-updated', data.data);
        }
      }
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to import qualifications: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      importing.value = false;
    }
  }

  function closeImportDialog() {
    showImportDialog.value = false;
    selectedImportIds.value = [];
  }

  // Watch for import dialog opening
  watch(showImportDialog, (newValue) => {
    if (newValue) {
      loadLibraryQualifications();
    }
  });
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.import-content {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
